import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import moment from "moment";
import { connect } from "react-redux";
import { socketListen } from "../../appRedux/ducks/socket";
import { Helmet } from "react-helmet";
import Summary from "./Summary";
import "./index.scss";
import { RangePicker } from "../Earnings/components/calendar.tsx";
import { IconButton, makeStyles, Tooltip } from "@material-ui/core";
import { searchLoad } from "../../appRedux/ducks/search";
import OptionTickerSearch from "../OptionScanner/OptionTickerSearch";
import useDebounce from "../OptionScanner/useDebounce";
import { Link } from "react-router-dom";
import "../common/Tables/liveOptionsTable.css";
import "../common/Tables/animations/addedRowsAnimations.scss";
import InfiniteScrollLiveOptionsTable from "./InfiniteScrollLiveOptionsTable";
import { symbolSetter } from "../../appRedux/ducks/symbol";
import {
  liveOptionsLoad,
  liveOptionsLoadMore,
  getInfiniteScrollData,
  getHasMore,
  getIsLoading,
} from "../../appRedux/ducks/newLiveOptions";
import { abbreviate } from "../homePage/TradeAlgoCard";
import { useMedia } from "react-media";
import clsx from "clsx";
import { useHistory, useParams } from "react-router-dom";
import BidAskSelect from "./BidAskSelect";
import usePrevious from "../../hooks/usePrevious";
import BBi from "../../assets/images/liveOptions/BBi.png";
import AAi from "../../assets/images/liveOptions/AAi.png";
import LAi from "../../assets/images/liveOptions/LAi.png";
import LBi from "../../assets/images/liveOptions/LBi.png";
import { TableSkeleton } from "../common/Skeletons/TableSkeleton";
import CustomSlider from "./CustomSlider.js";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useAuth } from "../../contexts/Auth";

// Custom hook for hold-to-repeat button functionality
const useHoldToRepeat = (
  callback,
  { initialDelay = 500, repeatDelay = 100 } = {},
) => {
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const start = useCallback(() => {
    callback();
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(callback, repeatDelay);
    }, initialDelay);
  }, [callback, initialDelay, repeatDelay]);

  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  return {
    onMouseDown: start,
    onMouseUp: stop,
    onMouseLeave: stop,
    onTouchStart: start,
    onTouchEnd: stop,
  };
};

const useStyles = makeStyles((theme) => ({
  background: {
    backgroundColor: theme.palette.primary.tableBackground + " !important",
  },
  LA: {
    backgroundColor: "rgba(220, 0, 78, 0.8)",
  },
  LB: {
    backgroundColor: "#EA580C",
  },
  BB: {
    backgroundColor: "#2563EB",
  },
  AA: {
    backgroundColor: " #0D9488",
  },
  LAi: {
    color: "rgba(220, 0, 78, 0.8)",
  },
  LBi: {
    color: "#EA580C",
  },
  BBi: {
    color: "#2563EB",
  },
  AAi: {
    color: " #0D9488",
  },
  text: {
    color: theme.palette.primary.text + " !important",
  },
  popper: {
    "& .MuiSelectUnstyled-root": {
      background: theme.palette.primary.select,
      color: theme.palette.primary.text,
    },
    "& .MuiSelectUnstyled-listbox ": {
      fontFamily: "IBM Plex Sans,sans-serif",
      fontSize: "14px",
      boxSizing: "borderBox",
      padding: "5px",
      margin: "0px 0",
      background: "#F1F5F9",
      borderRadius: "24px",
      color: "black",
      overflow: "auto",
      outline: "0px",
    },
  },
}));

const mediaQuery = {
  isMobile: "screen and (max-width: 991px)",
  isResponsiveHeaderCell: "screen and (min-width: 1800px)",
};

const valuetext = (value) => {
  return <span className="slider-tooltip"> {abbreviate(value, 2)} </span>;
};
const valuetextExpire = (value) => {
  return (
    <span className="slider-tooltip">
      {" "}
      {`${value} Day${value === 1 ? "" : "s"}`}{" "}
    </span>
  );
};
const valuetextPricePerContract = (value) => {
  return <span className="slider-tooltip"> ${value.toFixed(2)} </span>;
};

const today = new Date();

const InfiniteScrollLiveOptionsContainer = ({
  themeMode,
  listen,
  searchedSymbols,
  loading,
  searchAllSymbols,
  company = false,
  symbols,
  setSymbolRedux,
  liveFetchOptions,
  loadMoreOptions,
  infiniteScrollData,
  optionsLoading,
  excludeSymbols,
  firstRender,
  defaultTickersSettings,
  minPricePerContractSettings,
  hasMore,
}) => {
  const { isMobile, isResponsiveHeaderCell } = useMedia({
    queries: mediaQuery,
  });
  const classes = useStyles();
  const { isBlurred } = useAuth();
  const [bidAsk, setBidAsk] = useState([]);
  const params = useParams();
  const all = params["0"]?.split("/") || {};
  const allParams = useMemo(() => {
    if (!company) {
      if (all[4]) {
        let [prem, expiryRange, minContract, tickers, dateRange] = all;
        let [minExpiry, maxExpiry] = expiryRange.split(",").map(Number);
        return {
          prem,
          minExpiry,
          maxExpiry,
          minContract,
          tickers,
          dateRange,
        };
      } else if (all[3]) {
        let [prem, expiryRange, minContract, tickers] = all;
        let [minExpiry, maxExpiry] = expiryRange.split(",").map(Number);
        return {
          prem,
          minExpiry,
          maxExpiry,
          minContract,
          tickers,
        };
      } else if (all[2]) {
        let [prem, expiryRange, minContract] = all;
        let [minExpiry, maxExpiry] = expiryRange.split(",").map(Number);
        return {
          prem,
          minExpiry,
          maxExpiry,
          minContract,
        };
      } else {
        let [prem, expiryRange] = all;
        let [minExpiry, maxExpiry] = expiryRange.split(",").map(Number);
        return {
          prem,
          minExpiry,
          maxExpiry,
        };
      }
    }
  }, [all, company]);

  const history = useHistory();
  const [minPremium, setMinPremium] = useState(
    allParams?.prem ? Number(allParams?.prem) : 35000,
  );
  const [contractMin, setContractMin] = useState(
    allParams?.minContract ? Number(allParams?.minContract) : 0,
  );
  const [expiryRange, setExpiryRange] = useState(
    !company && !!String(allParams?.minExpiry) && !!String(allParams?.maxExpiry)
      ? [Number(allParams.minExpiry), Number(allParams.maxExpiry)]
      : [0, 730],
  );
  const debouncedContractMin = useDebounce(contractMin, 400);
  const debouncedExpire = useDebounce(expiryRange, 400);
  const debouncedPrem = useDebounce(minPremium, 400);
  const [pricePerContractMin, setPricePerContractMin] = useState(
    minPricePerContractSettings ?? 0.1,
  );
  const debouncedPricePerContractMin = useDebounce(pricePerContractMin, 400);
  const [show, setShow] = useState(false);
  const [rangeString, setRangeString] = useState("");
  const [dateRange, setDateRange] = useState({
    startDate: today,
    endDate: today,
    key: "selection",
  });

  const dateRangeString = useMemo(() => {
    const todayMoment = moment().format("YYYY-MM-DD");
    if (
      moment(dateRange.startDate).format("YYYY-MM-DD") === todayMoment &&
      moment(dateRange.endDate).format("YYYY-MM-DD") === todayMoment
    ) {
      return "Today";
    } else {
      setBidAsk([]);
      return `${moment(dateRange.startDate).format("DD MMM, YYYY")} - ${moment(
        dateRange.endDate,
      ).format("DD MMM, YYYY")}`;
    }
  }, [dateRange]);
  const prevRange = usePrevious(dateRangeString);
  const prevBidAsk = usePrevious(bidAsk);
  const [symbol, setSymbol] = useState("");
  const [tickerString, setTickerString] = useState(
    allParams?.tickers ? allParams?.tickers : "",
  );
  const [ticker, setTicker] = useState(
    allParams?.tickers ? all[3].split(",").map((ticker) => ({ ticker })) : [],
  );
  const [excludeTickerString, setExcludeTickerString] = useState("");
  const [excludeTickers, setExcludeTickers] = useState([]);
  const [excludeSymbol, setExcludeSymbol] = useState("");
  const animatedRowsRef = useRef(new Set()); // Track animated rows without causing re-renders
  const animationTimeoutsRef = useRef(new Map()); // Track animation timeouts
  const userInteractedWithTickers = useRef(false); // Track if user has manually modified tickers

  const [sortModel, setSortModel] = useState([
    {
      field: "time",
      sort: "desc",
    },
  ]);

  const hide = useMemo(() => {
    return (
      company &&
      !optionsLoading &&
      minPremium === 35000 &&
      expiryRange[1] === 730 &&
      infiniteScrollData.length === 0 &&
      dateRangeString === `Today's Contracts` &&
      bidAsk === "1"
    );
  }, [
    company,
    debouncedPrem,
    debouncedExpire,
    infiniteScrollData,
    dateRangeString,
    bidAsk,
  ]);

  const rowClassName = useCallback(
    (params) => {
      // Only show animation if:
      // 1. Item has newItem flag
      // 2. Item was added within the last 3 seconds
      // 3. We're near the top of the table (where new items appear)
      if (params?.row?.newItem && params?.row?.newItemTimestamp) {
        const itemId = params.row.id;
        const timeSinceAdded = Date.now() - params.row.newItemTimestamp;
        const isRecentlyAdded = timeSinceAdded < 3000; // 3 seconds

        // Check if we're viewing the top portion of the data
        const rowIndex = params.rowIndex || 0;
        const isNearTop = rowIndex < 20; // Only animate top 20 rows

        if (isRecentlyAdded && isNearTop) {
          // Add to animated rows set if not already there
          if (!animatedRowsRef.current.has(itemId)) {
            animatedRowsRef.current.add(itemId);

            // Set a timeout to remove the animation class after 3 seconds
            const timeout = setTimeout(() => {
              animatedRowsRef.current.delete(itemId);
              animationTimeoutsRef.current.delete(itemId);
            }, 3000);

            animationTimeoutsRef.current.set(itemId, timeout);
          }

          return `new-item-${themeMode}`;
        }
      }
      return "";
    },
    [themeMode],
  );

  const IconAndTooltip = useCallback(
    (bid, ask, fillPrice) => {
      let text = "";
      let tooltip = "";
      let Icon = null;

      if (ask < fillPrice) {
        text = "AA";
        tooltip = "Above Ask, Price Filled above Ask";
        Icon = AAi;
      } else if (ask === fillPrice) {
        text = "A";
        tooltip = "At Ask, Price Filled at Ask";
        Icon = LBi;
      } else if (ask - fillPrice < fillPrice - bid) {
        text = "LA";
        tooltip = "Leaning Ask, Price Filled leaning Ask";
        Icon = LAi;
      } else if (bid > fillPrice) {
        text = "BB";
        tooltip = "Below Bid, Price Filled below Bid";
        Icon = BBi;
      } else if (bid === fillPrice) {
        text = "AB";
        tooltip = "At Bid, Price Filled at Bid";
        Icon = BBi;
      } else if (ask - fillPrice > fillPrice - bid) {
        text = "LB";
        tooltip = "Leaning Bid, Price Filled leaning Bid";
        Icon = LBi;
      }

      return (
        <Tooltip
          classes={{
            tooltip: classes[text],
            arrow: classes[`${text}i`],
          }}
          className="align-center d-flex"
          title={tooltip}
          placement="top"
          arrow
        >
          <span
            style={{ position: "relative", left: isMobile ? "-6px" : "" }}
            className={`bid-ask-base ${text}`}
          >
            {text}
            {Icon && <img src={Icon} className="BBi" alt={text} />}
          </span>
        </Tooltip>
      );
    },
    [isMobile],
  );

  const columns = useMemo(() => {
    let base = [
      {
        field: "time",
        headerName: "Time",
        renderHeader: () => <span className="table-headers2">Time</span>,
        renderCell: (params) => (
          <span
            className={clsx(
              classes.text,
              `time  ${!isMobile && "large-table"}`,
            )}
          >
            {moment(Number(params.value) * 1000).isSame(moment(), "Day")
              ? moment
                  .tz(Number(params.value) * 1000, "America/New_York")
                  .format("hh:mm:ss A")
              : moment
                  .tz(Number(params.value) * 1000, "America/New_York")
                  .format("MM/DD hh:mm:ss A")}
          </span>
        ),
        flex: 2.6,
        sortComparator: (v1, v2) => Number(v1) - Number(v2),
      },
      {
        field: "ticker",
        headerName: "Ticker",
        renderHeader: () => <span className="table-headers">Ticker</span>,
        renderCell: (params) => (
          <span
            className="ticker"
            style={{ position: "relative", left: "-9px" }}
            onClick={() => {
              setSymbolRedux({
                type: "",
                value: params.value,
              });
            }}
          >
            <Link
              to={`/company/${params.value}`}
              className={clsx(classes.text, "ticker")}
            >
              <span
                className={clsx(
                  classes.text,
                  "ticker",
                  isBlurred ? "blurred" : "",
                )}
              >
                {params.value}
              </span>
            </Link>
          </span>
        ),
        flex: 1,
      },
      {
        field: "exp",
        headerName: "Expiry",
        flex: 1,
        renderHeader: () => <span className="table-headers">Expiry</span>,
        renderCell: (params) => (
          <span className={clsx(classes.text, "strike")}>
            {moment.utc(params.value).format("MM/DD/YY")}
          </span>
        ),
      },
      {
        field: "strike",
        headerName: "Strike",
        renderHeader: () => <span className="table-headers">Strike</span>,
        renderCell: (params) => (
          <span className={clsx(classes.text, "strike")}>{params.value}</span>
        ),
        flex: 0.8,
        sortComparator: (v1, v2) => Number(v1) - Number(v2),
      },
      {
        field: "cp",
        headerName: "C/P",
        flex: 0.8,
        renderHeader: () => <span className="table-headers">C/P</span>,
        renderCell: (params) => (
          <span
            className={`cp ${params.value === "C" ? "green-call" : "red-put"}`}
          >
            {params.value === "C" ? "Calls" : "Puts"}
          </span>
        ),
      },
      {
        field: "spot",
        headerName: "Spot",
        flex: 0.8,
        renderHeader: () => <span className="table-headers">Spot</span>,
        renderCell: (params) => (
          <span className={classes.text} style={{ marginLeft: "3px" }}>
            {Number(params.value).toFixed(2)}
          </span>
        ),
        sortComparator: (v1, v2) => Number(v1) - Number(v2),
      },
      {
        field: "bid_price",
        headerName: "Bid",
        flex: 0.8,
        renderHeader: () => <span className="table-headers">Bid</span>,
        renderCell: (params) => (
          <span className={classes.text} style={{ marginLeft: "3px" }}>
            {Number(params.value).toFixed(2)}
          </span>
        ),
        sortComparator: (v1, v2) => Number(v1) - Number(v2),
      },
      {
        field: "contract_quantity",
        headerName: "Details",
        renderHeader: () => <span className="table-headers">Details</span>,
        renderCell: (params) => {
          return (
            <span
              style={{ position: "relative", left: "-3px" }}
              className={clsx(
                classes.text,
                `ppc ${isMobile ? "" : "d-flex align-center"}`,
              )}
            >
              {Math.round(params.row.contract_quantity) +
                " @ " +
                Number(params?.row?.price_per_contract || 0)?.toFixed(2) || ""}
              {isMobile && <br />}
              {IconAndTooltip(
                Number(params.row.bid_price),
                Number(params.row.ask_price),
                Number(params.row.price_per_contract),
              )}
            </span>
          );
        },
        flex: 2,
      },
      {
        field: "ask_price",
        headerName: "Ask",
        flex: 0.8,
        renderHeader: () => <span className="table-headers">Ask</span>,
        renderCell: (params) => (
          <span className={classes.text} style={{ marginLeft: "3px" }}>
            {Number(params.value).toFixed(2)}
          </span>
        ),
        sortComparator: (v1, v2) => Number(v1) - Number(v2),
      },
      {
        field: "type",
        headerName: "Type",
        renderHeader: () => <span className="table-headers">Type</span>,
        renderCell: (params) => (
          <span className={clsx(classes.text, "type-cell")}>
            {params.value}
          </span>
        ),
        flex: 1,
      },
      {
        field: "prem",
        headerName: "Premium",
        flex: 1.5,
        renderHeader: () => <span className="table-headers">Premium</span>,
        renderCell: (params) => (
          <span className="prem-cell">${abbreviate(params.value)}</span>
        ),
        sortComparator: (v1, v2) => Number(v1) - Number(v2),
      },
    ];
    if (!dateRangeString.includes("Today")) {
      base.splice(6, 1);
      base.splice(7, 1);
    }
    return base;
  }, [isMobile, dateRangeString, classes, bidAsk, isResponsiveHeaderCell]);

  const [open, setOpen] = useState(false);
  const debouncedExcludeSymbol = useDebounce(excludeSymbol, 500);
  const debouncedSymbol = useDebounce(symbol, 500);

  const prevTickerString = usePrevious(tickerString);
  const prevExcludeTickerString = usePrevious(excludeTickerString);

  const prevValues = useMemo(() => {
    return (
      prevBidAsk !== bidAsk ||
      prevRange !== dateRangeString ||
      prevTickerString !== tickerString ||
      prevExcludeTickerString !== excludeTickerString
    );
  }, [
    prevBidAsk,
    bidAsk,
    prevRange,
    dateRangeString,
    prevTickerString,
    tickerString,
    prevExcludeTickerString,
    excludeTickerString,
  ]);

  useEffect(() => {
    company
      ? listen([`options;${symbols}`, "CHAT"])
      : listen(["options", "CHAT", "TRADE_MESSAGE"]);
    return () => listen([]);
  }, [symbols, company]);

  useEffect(() => {
    return () => {
      liveFetchOptions(
        35000,
        730,
        false,
        "",
        "",
        true,
        sortModel[0],
        bidAsk,
        0,
        "",
        1,
        100,
        0.1,
      );
    };
  }, []);

  useEffect(() => {
    if (debouncedExcludeSymbol.length) {
      searchAllSymbols(debouncedExcludeSymbol, true);
    }
  }, [debouncedExcludeSymbol]);

  useEffect(() => {
    if (debouncedSymbol.length) {
      searchAllSymbols(debouncedSymbol);
    }
  }, [debouncedSymbol]);

  useEffect(() => {
    if (
      moment().format("YYYY-MM-DD") ===
      moment(dateRange.startDate).format("YYYY-MM-DD")
    ) {
      setRangeString("");
    } else {
      setRangeString(
        `${moment
          .tz(dateRange.startDate, "America/New_York")
          .format("YYYY-MM-DD")},${moment
          .tz(dateRange.endDate, "America/New_York")
          .format("YYYY-MM-DD")}`,
      );
    }
  }, [JSON.stringify(dateRange)]);

  useEffect(() => {
    if (tickerString.length) {
      setExcludeTickerString("");
      setExcludeTickers([]);
    }
  }, [tickerString]);

  useEffect(() => {
    if (!!firstRender && !company) {
      // Only set default tickers if:
      // 1. No tickers are in URL params
      // 2. Default tickers exist in settings
      // 3. User hasn't manually interacted with tickers
      // 4. User hasn't explicitly cleared tickers (check if URL has been manually set to just premium/expiry)
      if (
        !allParams?.tickers &&
        defaultTickersSettings?.length &&
        !userInteractedWithTickers.current
      ) {
        // Check if the current URL is just the basic format (premium/expiry only)
        // If so, don't auto-add default tickers as user might have intentionally cleared them
        const currentPath = history.location.pathname;
        const basicUrlPattern = /^\/live-options\/\d+\/\d+,\d+$/;

        if (!basicUrlPattern.test(currentPath)) {
          const tickers = defaultTickersSettings
            .split(",")
            .map((ticker) => ({ ticker }));
          setTicker(tickers);
          setTickerString(defaultTickersSettings);
        }
      }
    }
  }, [
    firstRender,
    company,
    defaultTickersSettings,
    allParams?.tickers,
    history.location.pathname,
  ]);

  // Sync pricePerContractMin from user settings on first render
  useEffect(() => {
    if (firstRender && minPricePerContractSettings !== undefined) {
      setPricePerContractMin(minPricePerContractSettings);
    }
  }, [firstRender, minPricePerContractSettings]);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShow(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  // Cleanup animation timeouts on unmount
  useEffect(() => {
    return () => {
      // Clear all animation timeouts when component unmounts
      animationTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      animationTimeoutsRef.current.clear();
      animatedRowsRef.current.clear();
    };
  }, []);

  // Main API call effect - ONLY for user filter changes
  useEffect(() => {
    if (company) {
      !!symbols &&
        liveFetchOptions(
          debouncedPrem,
          debouncedExpire,
          false,
          rangeString,
          symbols,
          prevValues,
          sortModel[0],
          bidAsk,
          debouncedContractMin,
          undefined,
          1,
          100,
          debouncedPricePerContractMin,
        );
    } else {
      liveFetchOptions(
        debouncedPrem,
        debouncedExpire,
        false,
        rangeString,
        tickerString,
        prevValues,
        sortModel[0],
        bidAsk,
        debouncedContractMin,
        excludeTickerString,
        1,
        100,
        debouncedPricePerContractMin,
      );
    }
  }, [
    // ONLY include user-controlled filters - NOT data that gets updated by socket
    debouncedPrem,
    debouncedExpire,
    rangeString,
    tickerString,
    company,
    symbols,
    sortModel,
    bidAsk,
    prevValues,
    debouncedContractMin,
    excludeTickerString,
    debouncedPricePerContractMin,
    // EXPLICITLY EXCLUDE infiniteScrollData to prevent socket-triggered calls
  ]);

  // Update URL when filters change
  useEffect(() => {
    if (!company) {
      // Build URL parts based on current filter values
      const urlParts = [];

      // Always include premium and expiry range
      urlParts.push(debouncedPrem);
      urlParts.push(`${debouncedExpire[0]},${debouncedExpire[1]}`);

      // Only include contract min if it's not 0
      const hasContractMin = debouncedContractMin > 0;
      // Only include tickers if they exist and are not empty
      const hasTickers = tickerString && tickerString.length > 0;
      // Only include date range if it exists
      const hasDateRange = rangeString && rangeString.length > 0;

      // Add contract min only if needed
      if (hasContractMin || hasTickers || hasDateRange) {
        urlParts.push(debouncedContractMin);
      }

      // Add tickers only if they exist or if we need to include date range after them
      if (hasTickers || hasDateRange) {
        urlParts.push(tickerString || "");
      }

      // Add date range only if it exists
      if (hasDateRange) {
        urlParts.push(rangeString);
      }

      // Construct the new URL
      const newUrl = `/live-options/${urlParts.join("/")}`;

      // Only update if the URL actually changed
      const currentPath = history.location.pathname;
      if (currentPath !== newUrl) {
        history.replace(newUrl);
      }
    }
  }, [
    debouncedPrem,
    debouncedExpire,
    debouncedContractMin,
    tickerString,
    rangeString,
    company,
    history,
  ]);

  const handleIncrementPrem = useCallback(() => {
    setMinPremium((prev) => prev + 10000);
  }, []);

  const handleDecrementPrem = useCallback(() => {
    setMinPremium((prev) => Math.max(prev - 10000, 35000));
  }, []);

  const handleIncrementContract = useCallback(() => {
    setContractMin((prev) => prev + 10);
  }, []);

  const handleDecrementContract = useCallback(() => {
    setContractMin((prev) => Math.max(prev - 10, 0));
  }, []);

  const handleIncrementPrice = useCallback(() => {
    setPricePerContractMin((prev) => prev + 0.1);
  }, []);

  const handleDecrementPrice = useCallback(() => {
    setPricePerContractMin((prev) => Math.max(prev - 0.1, 0));
  }, []);

  // Hold-to-repeat bindings for all +/- buttons
  const incrementPremBindings = useHoldToRepeat(handleIncrementPrem);
  const decrementPremBindings = useHoldToRepeat(handleDecrementPrem);
  const incrementContractBindings = useHoldToRepeat(handleIncrementContract);
  const decrementContractBindings = useHoldToRepeat(handleDecrementContract);
  const incrementPriceBindings = useHoldToRepeat(handleIncrementPrice);
  const decrementPriceBindings = useHoldToRepeat(handleDecrementPrice);

  const handleChangePrem = (event, newValue) => {
    if (typeof newValue === "number") {
      setMinPremium(newValue);
    }
  };

  const handleChangeContractMin = (event, newValue) => {
    if (typeof newValue === "number") {
      setContractMin(newValue);
    }
  };

  const handleChangeExpire = (event, newValue) => {
    if (Array.isArray(newValue)) {
      setExpiryRange(newValue);
    }
  };

  const handleChangePricePerContract = (event, newValue) => {
    if (typeof newValue === "number") {
      setPricePerContractMin(newValue);
    }
  };

  // Wrapper function to track user interaction with tickers
  const handleSetTicker = (newTickers) => {
    userInteractedWithTickers.current = true;
    setTicker(newTickers);
  };

  // Wrapper function to track user interaction with ticker string
  const handleSetTickerString = (newTickerString) => {
    userInteractedWithTickers.current = true;
    setTickerString(newTickerString);
  };

  // Wrapper function for exclude tickers (also marks user interaction)
  const handleSetExcludeTickers = (newTickers) => {
    userInteractedWithTickers.current = true;
    setExcludeTickers(newTickers);
  };

  const handleSetExcludeTickerString = (newTickerString) => {
    userInteractedWithTickers.current = true;
    setExcludeTickerString(newTickerString);
  };

  return (
    <>
      {!company && (
        <Helmet>
          <title>Live Options | TradeAlgo </title>
        </Helmet>
      )}{" "}
      {hide ? null : (
        <div style={{ padding: "5px 0" }}>
          <div className="ticker-search-container">
            {!company && (
              <div className={"live-option-search-filter"}>
                <OptionTickerSearch
                  open={open}
                  setOpen={setOpen}
                  valueLabelDisplay="on"
                  options={searchedSymbols}
                  value={symbol}
                  setSymbol={setSymbol}
                  symbol={symbol}
                  tickers={ticker}
                  loading={loading}
                  setTicker={handleSetTicker}
                  setTickerString={handleSetTickerString}
                  customMinWidth={200}
                />
              </div>
            )}

            {!!(!tickerString.length && !company) && (
              <div className={"live-option-search-filter"}>
                <OptionTickerSearch
                  exclude={true}
                  open={open}
                  setOpen={setOpen}
                  valueLabelDisplay="on"
                  options={excludeSymbols}
                  value={excludeSymbol}
                  setSymbol={setExcludeSymbol}
                  symbol={excludeSymbol}
                  tickers={excludeTickers}
                  loading={loading}
                  setTicker={handleSetExcludeTickers}
                  setTickerString={handleSetExcludeTickerString}
                  customMinWidth={200}
                />
              </div>
            )}

            <div className="live-option-search-filter ">
              <BidAskSelect
                themeMode={themeMode}
                value={bidAsk}
                setValue={setBidAsk}
              />
            </div>
          </div>

          <div className="contents">
            {company && (
              <div className={`contents-title contents-title-${themeMode}`}>
                Contracts Summary
              </div>
            )}
            <Summary />
          </div>

          <div
            className={`row live-options-filter-container ${show ? "rangepicker-open" : ""}`}
          >
            <div
              style={{ zIndex: show ? 2 : 1 }}
              className={"live-option-date-filter"}
            >
              <span className={"dates"}>
                <span className={`dates-label dates-label-${themeMode}`}>
                  Contracts
                </span>
                <div
                  className="live-option-date-selector"
                  onClick={() => setShow(!show)}
                >
                  <span>Date: {dateRangeString}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M7.5 2H6V4C3.79086 4 2 5.79086 2 8V18C2 20.2091 3.79086 22 6 22H18C20.2091 22 22 20.2091 22 18V8C22 5.79086 20.2091 4 18 4V2H16.5V4H7.5V2ZM6 5.5H18C19.2095 5.5 20.2184 6.35888 20.45 7.5H3.55001C3.78164 6.35888 4.79052 5.5 6 5.5ZM3.5 9V18C3.5 19.3807 4.61929 20.5 6 20.5H18C19.3807 20.5 20.5 19.3807 20.5 18V9H3.5Z"
                      fill="black"
                      fillOpacity="0.56"
                    />
                  </svg>
                </div>
                {show && (
                  <div
                    ref={wrapperRef}
                    className="live-option-date-rangepicker"
                  >
                    <RangePicker
                      style={{ gridArea: "1 / 1 / 2 / 2" }}
                      selectedRange={dateRange}
                      onRangeSelect={(r) => {
                        setDateRange(r);
                        if (
                          moment(r.endDate).diff(moment(r.startDate), "days") >=
                          1
                        ) {
                          setTimeout(() => {
                            setShow(!show);
                          }, 500);
                        }
                      }}
                    />
                  </div>
                )}
              </span>
            </div>

            <div
              className="live-options-slider-container"
              style={{ zIndex: show ? 1 : 2 }}
            >
              <div className="live-options-slider">
                <CustomSlider
                  value={minPremium}
                  min={35000}
                  step={1000}
                  max={10000000}
                  valueLabelDisplay="auto"
                  valueLabelFormat={valuetext}
                  onChange={handleChangePrem}
                  aria-labelledby="non-linear-slider"
                />
                <div className="slider-label">
                  <div className={`min-prem-value-${themeMode}`}>
                    {abbreviate(35000)}
                  </div>
                  <Tooltip title="Click to decrement the minimum premium by 10k">
                    <IconButton
                      aria-label="decrement-premium"
                      size="medium"
                      {...decrementPremBindings}
                    >
                      <RemoveIcon fontSize="inherit" color="success" />
                    </IconButton>
                  </Tooltip>
                  <div className={`min-prem-${themeMode}`}>Minimum Premium</div>
                  <Tooltip title="Click to increment the minimum premium by 10k">
                    <IconButton
                      aria-label="increment-premium"
                      size="medium"
                      {...incrementPremBindings}
                    >
                      <AddIcon fontSize="inherit" color="success" />
                    </IconButton>
                  </Tooltip>
                  <div className={`min-prem-value-${themeMode}`}>
                    {abbreviate(10000000)}
                  </div>
                </div>
              </div>
              <div className="live-options-slider up-7">
                <CustomSlider
                  value={expiryRange}
                  min={0}
                  step={1}
                  max={365 * 2}
                  valueLabelDisplay="off"
                  valueLabelFormat={valuetextExpire}
                  onChange={handleChangeExpire}
                  aria-labelledby="range-slider"
                />
                <div className="slider-label mt-15">
                  <div className={`exp-within-value-${themeMode}`}>Today</div>
                  <div className={`exp-within-${themeMode}`}>
                    Expires Within
                  </div>
                  <div className={`exp-within-value-${themeMode}`}>
                    730 days
                  </div>
                </div>
              </div>
              <div className="live-options-slider">
                <CustomSlider
                  value={contractMin}
                  min={0}
                  step={1}
                  max={100000}
                  valueLabelFormat={valuetext}
                  onChange={handleChangeContractMin}
                  aria-labelledby="contract-min-slider"
                />
                <div className="slider-label">
                  <div className={`exp-within-value-${themeMode}`}>0</div>
                  <Tooltip title="Click to decrement the minimum contracts by 10">
                    <IconButton
                      aria-label="decrement-contracts"
                      size="medium"
                      {...decrementContractBindings}
                    >
                      <RemoveIcon fontSize="inherit" color="success" />
                    </IconButton>
                  </Tooltip>
                  <div className={`exp-within-${themeMode}`}>
                    Minimum Contracts
                  </div>
                  <Tooltip title="Click to increment the minimum contracts by 10">
                    <IconButton
                      aria-label="increment-contracts"
                      size="medium"
                      {...incrementContractBindings}
                    >
                      <AddIcon fontSize="inherit" color="success" />
                    </IconButton>
                  </Tooltip>
                  <div className={`exp-within-value-${themeMode}`}>100K</div>
                </div>
              </div>
              <div className="live-options-slider">
                <CustomSlider
                  value={pricePerContractMin}
                  min={0}
                  step={0.01}
                  max={500}
                  valueLabelDisplay="auto"
                  valueLabelFormat={valuetextPricePerContract}
                  onChange={handleChangePricePerContract}
                  aria-labelledby="price-per-contract-min-slider"
                />
                <div className="slider-label">
                  <div className={`exp-within-value-${themeMode}`}>$0</div>
                  <Tooltip title="Click to decrement min price per contract by $0.10">
                    <IconButton
                      aria-label="decrement-price"
                      size="medium"
                      {...decrementPriceBindings}
                    >
                      <RemoveIcon fontSize="inherit" color="success" />
                    </IconButton>
                  </Tooltip>
                  <div className={`exp-within-${themeMode}`}>
                    Min Price/Contract
                  </div>
                  <Tooltip title="Click to increment min price per contract by $0.10">
                    <IconButton
                      aria-label="increment-price"
                      size="medium"
                      {...incrementPriceBindings}
                    >
                      <AddIcon fontSize="inherit" color="success" />
                    </IconButton>
                  </Tooltip>
                  <div className={`exp-within-value-${themeMode}`}>$500</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-12 col-lg-12 col-md-12 col-sm-12">
            {optionsLoading && !infiniteScrollData.length ? (
              <TableSkeleton />
            ) : (
              <InfiniteScrollLiveOptionsTable
                sortModel={sortModel}
                setSortModel={setSortModel}
                rows={infiniteScrollData}
                columns={columns}
                loading={optionsLoading}
                hasRowClassName={true}
                rowClassName={rowClassName}
                hasMore={hasMore}
                onLoadMore={() => {
                  if (company) {
                    loadMoreOptions(
                      debouncedPrem,
                      debouncedExpire,
                      rangeString,
                      symbols,
                      sortModel[0],
                      bidAsk,
                      debouncedContractMin,
                      undefined,
                      100,
                      debouncedPricePerContractMin,
                    );
                  } else {
                    loadMoreOptions(
                      debouncedPrem,
                      debouncedExpire,
                      rangeString,
                      tickerString,
                      sortModel[0],
                      bidAsk,
                      debouncedContractMin,
                      excludeTickerString,
                      100,
                      debouncedPricePerContractMin,
                    );
                  }
                }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

const stateToProps = (state) => ({
  searchedSymbols: state.searchSymbols.searchSymbols,
  excludeSymbols: state.searchSymbols.excludeSymbols,
  loading: state.searchSymbols.symbolsLoading,
  symbols: state.symbol.symbol.value,
  infiniteScrollData: getInfiniteScrollData(state),
  hasMore: getHasMore(state),
  optionsLoading: getIsLoading(state),
  themeMode: state.themeMode.themeMode,
  defaultTickersSettings:
    state.userSettings.userSettings.defaultLiveOptionsTickers,
  minPricePerContractSettings:
    state.userSettings.userSettings.minPricePerContract,
  firstRender: state.userSettings.firstRender,
});

const dispatchToProps = (dispatch) => ({
  searchAllSymbols: (symbol, exclude = false) =>
    dispatch(searchLoad(symbol, exclude)),
  listen: (channels) => dispatch(socketListen(channels)),
  setSymbolRedux: (symbol) => dispatch(symbolSetter(symbol)),
  liveFetchOptions: (
    prem_min,
    exp_within,
    loadMore,
    date,
    tickers,
    clearCache,
    sortModel,
    bidAskFilter,
    contract_quantity_min,
    exclude,
    page,
    pageSize,
    price_per_contract_min,
  ) =>
    dispatch(
      liveOptionsLoad(
        prem_min,
        exp_within,
        loadMore,
        date,
        tickers,
        clearCache,
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        page,
        pageSize,
        price_per_contract_min,
      ),
    ),
  loadMoreOptions: (
    prem_min,
    exp_within,
    date,
    tickers,
    sortModel,
    bidAskFilter,
    contract_quantity_min,
    exclude,
    pageSize,
    price_per_contract_min,
  ) =>
    dispatch(
      liveOptionsLoadMore(
        prem_min,
        exp_within,
        date,
        tickers,
        sortModel,
        bidAskFilter,
        contract_quantity_min,
        exclude,
        pageSize,
        price_per_contract_min,
      ),
    ),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(InfiniteScrollLiveOptionsContainer);
